import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import landingService from '../../services/landingService';
import "./LandingNavbar.css";

const LandingNavbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  // Handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('nav-menu-open');
    } else {
      document.body.classList.remove('nav-menu-open');
    }
    return () => {
      document.body.classList.remove('nav-menu-open');
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (e) => {
      // Check if click is outside both nav and mobile menu
      const isOutsideNav = navRef.current && !navRef.current.contains(e.target);
      const isOutsideMobileMenu = mobileMenuRef.current && !mobileMenuRef.current.contains(e.target);
      
      if (isOutsideNav && isOutsideMobileMenu) {
        setActiveDropdown(null);
        setMobileMenuOpen(false);
      }
    };

    // Close mobile menu on resize to desktop
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = (menu) => {
    // Only use hover on desktop
    if (window.innerWidth > 768) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setActiveDropdown(menu);
    }
  };

  const handleMouseLeave = () => {
    // Only use hover on desktop
    if (window.innerWidth > 768) {
      timeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 150);
    }
  };

  const handleDropdownClick = (menu) => {
    // Toggle on mobile, or click to open on desktop
    if (window.innerWidth <= 768) {
      setActiveDropdown(activeDropdown === menu ? null : menu);
    }
  };

  const handleLinkClick = () => {
    // Close mobile menu when clicking a link
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const featuresItems = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      title: "Research Papers",
      description: "Access 200M+ papers with AI-powered search",
      tag: "Core",
      to: "/features#research-papers",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
      ),
      title: "Research Labs",
      description: "Private collaboration spaces for your team",
      tag: "Popular",
      to: "/features#research-labs",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
        </svg>
      ),
      title: "Communities",
      description: "Join specialized research communities",
      tag: null,
      to: "/features#communities",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
        </svg>
      ),
      title: "Courses",
      description: "Learn from expert-led research courses",
      tag: "New",
      to: "/features#courses",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
        </svg>
      ),
      title: "Workshops",
      description: "Attend live research workshops",
      tag: null,
      to: "/features#workshops",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
        </svg>
      ),
      title: "Messaging",
      description: "Real-time collaboration chat",
      tag: null,
      to: "/features#messaging",
    },
  ];

  const resourcesItems = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
        </svg>
      ),
      title: "Blog",
      description: "Latest updates and insights",
      to: "/blog",
      external: false,
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      title: "Documentation",
      description: "Learn how to use the platform",
      to: "/docs",
      external: false,
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      title: "Research Guides",
      description: "Best practices for researchers",
      to: "/guides",
      external: false,
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
        </svg>
      ),
      title: "Help Center",
      description: "Get support when you need it",
      to: "/help",
      external: false,
    },
  ];

  const [resourceFeatured, setResourceFeatured] = useState(null);

  useEffect(() => {
    const fetchFeaturedBlog = async () => {
      try {
        const result = await landingService.getBlogPosts({ limit: 1 });
        if (result.success && result.posts?.length > 0) {
          const post = result.posts[0];
          setResourceFeatured({
            tag: 'Latest Post',
            title: post.title,
            description: post.excerpt || post.summary || post.content?.substring(0, 140) + '...',
            to: `/blog/${post.slug}`,
            image: post.coverImage || post.image || null,
          });
        }
      } catch (err) { /* ignored */ }
    };
    fetchFeaturedBlog();
  }, []);

  const companyItems = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
        </svg>
      ),
      title: "About Us",
      description: "Our mission and team",
      to: "/about",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
        </svg>
      ),
      title: "Careers",
      description: "Join our growing team",
      to: "/careers",
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      ),
      title: "Contact",
      description: "Get in touch with us",
      to: "/contact",
    },
  ];

  return (
    <>
      <nav ref={navRef} className={`landing-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="landing-nav-container">
          {/* Logo */}
          <Link to="/" className="landing-nav-logo">
            <span className="logo-text">research pro</span>
          </Link>

          {/* Navigation Links - Desktop Only */}
          <div className="landing-nav-links">
            {/* Features Dropdown */}
            <div
              className="nav-dropdown-wrapper"
              onMouseEnter={() => handleMouseEnter("features")}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className={`nav-link ${activeDropdown === "features" ? "active" : ""}`}
                onClick={(e) => {
                  if (e.target.closest('.chevron') || e.target.classList.contains('chevron')) {
                    handleDropdownClick("features");
                  } else {
                    handleLinkClick();
                    navigate('/features');
                  }
                }}
              >
                Features
                <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" onClick={(e) => { e.stopPropagation(); handleDropdownClick("features"); }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className={`dropdown-panel features-panel ${activeDropdown === "features" ? "open" : ""}`}>
                <div className="dropdown-content">
                  <div className="dropdown-grid">
                    {featuresItems.map((item, index) => (
                      <Link key={index} to={item.to} className="dropdown-item" onClick={handleLinkClick}>
                        <div className="dropdown-item-icon">{item.icon}</div>
                        <div className="dropdown-item-content">
                          <div className="dropdown-item-header">
                            <span className="dropdown-item-title">{item.title}</span>
                            {item.tag && <span className={`dropdown-item-tag ${item.tag.toLowerCase()}`}>{item.tag}</span>}
                          </div>
                          <span className="dropdown-item-desc">{item.description}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Resources Dropdown */}
            <div
              className="nav-dropdown-wrapper"
              onMouseEnter={() => handleMouseEnter("resources")}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className={`nav-link ${activeDropdown === "resources" ? "active" : ""}`}
                onClick={(e) => {
                  if (e.target.closest('.chevron') || e.target.classList.contains('chevron')) {
                    handleDropdownClick("resources");
                  } else {
                    handleLinkClick();
                    navigate('/resources');
                  }
                }}
              >
                Resources
                <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" onClick={(e) => { e.stopPropagation(); handleDropdownClick("resources"); }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className={`dropdown-panel resources-panel ${activeDropdown === "resources" ? "open" : ""}`}>
                <div className="dropdown-content">
                  <div className="dropdown-resources-layout">
                    <div className="dropdown-list">
                      {resourcesItems.map((item, index) => (
                        <Link key={index} to={item.to} className="dropdown-item" onClick={handleLinkClick}>
                          <div className="dropdown-item-icon">{item.icon}</div>
                          <div className="dropdown-item-content">
                            <div className="dropdown-item-header">
                              <span className="dropdown-item-title">{item.title}</span>
                              {item.external && (
                                <svg className="dropdown-item-external" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                                </svg>
                              )}
                            </div>
                            <span className="dropdown-item-desc">{item.description}</span>
                          </div>
                          <svg className="dropdown-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </Link>
                      ))}
                    </div>
                    {resourceFeatured && (
                      <div className="dropdown-featured">
                        <span className="dropdown-featured-tag">{resourceFeatured.tag}</span>
                        <h4 className="dropdown-featured-title">{resourceFeatured.title}</h4>
                        <p className="dropdown-featured-desc">{resourceFeatured.description}</p>
                        <Link to={resourceFeatured.to} className="dropdown-featured-link" onClick={handleLinkClick}>
                          Read more
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Company Dropdown */}
            <div
              className="nav-dropdown-wrapper"
              onMouseEnter={() => handleMouseEnter("company")}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className={`nav-link ${activeDropdown === "company" ? "active" : ""}`}
                onClick={(e) => {
                  if (e.target.closest('.chevron') || e.target.classList.contains('chevron')) {
                    handleDropdownClick("company");
                  } else {
                    handleLinkClick();
                    navigate('/company');
                  }
                }}
              >
                Company
                <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" onClick={(e) => { e.stopPropagation(); handleDropdownClick("company"); }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <div className={`dropdown-panel ${activeDropdown === "company" ? "open" : ""}`}>
                <div className="dropdown-content">
                  <div className="dropdown-list">
                    {companyItems.map((item, index) => (
                      <Link key={index} to={item.to} className="dropdown-item" onClick={handleLinkClick}>
                        <div className="dropdown-item-icon">{item.icon}</div>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">{item.title}</span>
                          <span className="dropdown-item-desc">{item.description}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Right Actions - Desktop */}
          <div className="landing-nav-actions">
            <Link to="/signin" className="nav-signin">
              Sign In
            </Link>
            <Link to="/signup" className="nav-cta">
              Get Started
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`mobile-toggle ${mobileMenuOpen ? "open" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Outside nav for proper rendering */}
      <div ref={mobileMenuRef} className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-content">
          {/* Features Accordion */}
          <div className="mobile-accordion">
            <button
              className={`mobile-accordion-trigger ${activeDropdown === "features" ? "active" : ""}`}
              onClick={() => handleDropdownClick("features")}
            >
              Features
              <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className={`mobile-accordion-content ${activeDropdown === "features" ? "open" : ""}`}>
              {featuresItems.map((item, index) => (
                <Link key={index} to={item.to} className="mobile-dropdown-item" onClick={handleLinkClick}>
                  <div className="mobile-item-icon">{item.icon}</div>
                  <div className="mobile-item-text">
                    <span className="mobile-item-title">{item.title}</span>
                    <span className="mobile-item-desc">{item.description}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Resources Accordion */}
          <div className="mobile-accordion">
            <button
              className={`mobile-accordion-trigger ${activeDropdown === "resources" ? "active" : ""}`}
              onClick={() => handleDropdownClick("resources")}
            >
              Resources
              <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className={`mobile-accordion-content ${activeDropdown === "resources" ? "open" : ""}`}>
              {resourcesItems.map((item, index) => (
                <Link key={index} to={item.to} className="mobile-dropdown-item" onClick={handleLinkClick}>
                  <div className="mobile-item-icon">{item.icon}</div>
                  <div className="mobile-item-text">
                    <span className="mobile-item-title">{item.title}</span>
                    <span className="mobile-item-desc">{item.description}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Company Accordion */}
          <div className="mobile-accordion">
            <button
              className={`mobile-accordion-trigger ${activeDropdown === "company" ? "active" : ""}`}
              onClick={() => handleDropdownClick("company")}
            >
              Company
              <svg className="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className={`mobile-accordion-content ${activeDropdown === "company" ? "open" : ""}`}>
              {companyItems.map((item, index) => (
                <Link key={index} to={item.to} className="mobile-dropdown-item" onClick={handleLinkClick}>
                  <div className="mobile-item-icon">{item.icon}</div>
                  <div className="mobile-item-text">
                    <span className="mobile-item-title">{item.title}</span>
                    <span className="mobile-item-desc">{item.description}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile CTA Buttons */}
          <div className="mobile-cta-section">
            <Link to="/signin" className="mobile-signin" onClick={handleLinkClick}>
              Sign In
            </Link>
            <Link to="/signup" className="mobile-signup" onClick={handleLinkClick}>
              Get Started
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingNavbar;
